import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "antd";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import {
  add,
  assignPermission,
  fetchAll,
  update,
} from "../../../core/data/redux/rolePermissionsSlice";
import { DateFormat } from "../../../core/hooks/dateFormat";
import { all_routes } from "../../router/all_routes";
import PermissionsAccordion from "./permissionsAccordion";
import PermissionGuard from "../../permissionGuard";
import { isSupportViewer } from "../../../utils/permissionUtils";
import { useT } from "../../../core/i18n";

const schema = Yup.object().shape({
  role_name: Yup.string()
    .required("role_name_is_required")
    .max(100, "role_name_max"),
});

const RolesPermissions = () => {
  const { t } = useT();
  const [checkedPermissions, setCheckedPermissions] = useState<number[]>([]);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const roleState = useSelector((state: any) => state.roleState);
  const routes = all_routes;
  const [searchText, setSearchText] = useState("");
  const [dataList, setDataList] = React.useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { role_name: "" },
  });

  const onSubmit = async (data: any) => {
    if (currentItem) {
      data["id"] = currentItem.id;
      await dispatch(update(data));
      setCurrentItem(null);
      setIsModalOpen(false);
      // Refresh data after update
      setTimeout(() => {
        fetchRecords();
      }, 500);
    } else {
      data["id"] = undefined;
      setIsModalOpen(false);
      await dispatch(add(data));
      // Refresh data after add
      setTimeout(() => {
        fetchRecords();
      }, 500);
    }
  };

  const fetchRecords = (search = searchText) => {
    dispatch(
      fetchAll({ page: pagination.current, limit: pagination.pageSize, roleName: search })
    );
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    dispatch(
      fetchAll({ page: 1, limit: pagination.pageSize, roleName: value })
    );
  };

  React.useEffect(() => {
    fetchRecords();
  }, []);

  React.useEffect(() => {
    if (
      roleState.loading === "fulfilled" &&
      roleState.action === "assign-permission"
    ) {
      setIsPermissionModalOpen(false);
      setCurrentItem(null);
      // Refresh data after permission assignment
      setTimeout(() => {
        fetchRecords();
      }, 500);
    } else if (
      roleState.loading === "fulfilled" &&
      (roleState.action === "update" || roleState.action === "add")
    ) {
      // Refresh data after update or add
      setTimeout(() => {
        fetchRecords();
      }, 500);
    } else if (
      roleState.loading === "fulfilled" &&
      roleState.action === "list"
    ) {
      const result = roleState?.data?.data || null;
      if (result.data && result.data.length > 0) {
        const roles = result?.data || [];
        setDataList(roles);
        setPagination({
          ...pagination,
          total: result.total || roles.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [roleState.loading]);

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords();
  };

  const columns = [
    {
      title: t("sr_no"),
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
                pagination.current * pagination.pageSize -
                pagination.pageSize +
                1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },
    {
      title: t("role_name"),
      dataIndex: "role_name",
      sorter: (a: any, b: any) => a.role_name.length - b.role_name.length,
    },

    {
      title: t("created_on"),
      dataIndex: "created_at",
      sorter: (a: any, b: any) => a.created_at.length - b.created_at.length,
      render: (created_at: any) => DateFormat(created_at) || "",
    },
    ...(!isSupportViewer()
      ? [
          {
            title: t("action"),
            dataIndex: "action",
            render: (text: string, record: any) => (
              <>
                <div className="d-flex align-items-center">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle  p-0 me-2"
                    onClick={() => {
                      setIsModalOpen(true);
                      setValue("role_name", record.role_name);
                      setCurrentItem(record);
                    }}
                  >
                    <i className="ti ti-edit-circle text-primary" />
                  </Link>
                  <Link
                    to=""
                    onClick={() => {
                      setCurrentItem(record);
                      setIsPermissionModalOpen(true);
                    }}
                    className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle  p-0 me-2"
                  >
                    <i className="ti ti-shield text-skyblue" />
                  </Link>
                </div>
              </>
            ),
          },
        ]
      : []),
  ];

  const handleSelect = (value: any) => {
    setCheckedPermissions(value);
  };
  const handlePermissionSubmit = async () => {
    await dispatch(
      assignPermission({
        permission_id: checkedPermissions,
        role_id: currentItem.id,
      })
    );
    // Refresh data after permission assignment
    setTimeout(() => {
      fetchRecords();
    }, 500);
  };
  return (
    <div>
      <Toasts
        show={roleState.loading === "rejected"}
        message={roleState.error}
        title={t("failed")}
        type={"danger"}
      />
      <Toasts
        show={
          roleState.loading === "fulfilled" &&
          roleState.action != "list" &&
          roleState.action != "get-permission"
        }
        message={roleState?.data?.message}
        title={t("success")}
        type={"success"}
      />
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">{t("roles_and_permissions")}</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>{t("dashboard")}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">{t("user_management")}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {t("roles_and_permissions")}
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="mb-2">
                  <PermissionGuard route="roles-permissions/add">
                    <Link
                      to="#"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={() => {
                        setCurrentItem(null);
                        setIsModalOpen(true);
                        setValue("role_name", "");
                      }}
                    >
                      <i className="ti ti-square-rounded-plus me-2" />
                      {t("add_role")}
                    </Link>
                  </PermissionGuard>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            {/* Filter Section */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3">{t("roles_and_permissions_list")}</h4>
              </div>
              <div className="card-body p-0 py-3">
                {/* Role Permission List */}
                <Table
                  columns={columns}
                  dataSource={dataList}
                  Selection={false}
                  paginationConfig={pagination}
                  handlePagination={handlePagination}
                  handleSearch={handleSearch}
                  searchValue={searchText}
                  searchPlaceholder={t("search_by_role_name")}
                  //loading={employeesState.loading === "pending"}
                />
                {/* /Role Permission List */}
              </div>
            </div>
            {/* /Filter Section */}
          </div>
        </div>
        {/* /Page Wrapper */}
        {/* Add Role */}

        <Modal
          title={t("assign_permission")}
          open={isPermissionModalOpen}
          onCancel={() => setIsPermissionModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <form style={{ marginTop: 25 }}>
            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-12">
                  <PermissionsAccordion
                    handleSelect={handleSelect}
                    role_id={currentItem?.id}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                to="#"
                className="btn btn-light me-2"
                onClick={() => {
                  setIsPermissionModalOpen(false);
                }}
              >
                {t("cancel")}
              </Link>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handlePermissionSubmit();
                }}
              >
                {t("submit")}
              </button>
            </div>
          </form>
        </Modal>

        <Modal
          title={currentItem ? t("edit_role") : t("add_role")}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body mb-3">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-0">
                    <label className="form-label">{t("role_name")}</label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("role_name")}
                    />
                    {errors.role_name && (
                      <small className="text-danger">
                        {t(errors.role_name.message || "")}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                to="#"
                className="btn btn-light me-2"
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                {t("cancel")}
              </Link>
              <button type="submit" className="btn btn-primary">
                {t("submit")}
              </button>
            </div>
          </form>
        </Modal>
        {/* /Add Role */}
        {/* Edit Role */}
        {/* <div className="modal fade" id="edit_role">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Edit Role</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-0">
                        <label className="col-form-label">Role Name</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue="Admin"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link to="#" className="btn btn-primary" data-bs-dismiss="modal">
                    Save Changes
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div> */}
        {/* /Edit Role */}
        {/* Delete Modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default RolesPermissions;
